import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Spinner, Table } from 'react-bootstrap';
import { shiftPreferenceAPI } from '../../services';

/**
 * #004452 — Tab "Ca ưu tiên" trong hồ sơ nhân viên (modal Xem chi tiết ở /employees).
 *
 * Góp ý Spot Fix: quản lý không có chỗ xem ca ưu tiên mà nhân viên (nhất là nhân viên mới) đã
 * đăng ký. Trước đây muốn xem phải sang màn "Duyệt ca ưu tiên" rồi tự tìm người; hồ sơ nhân viên
 * thì không có mục nào. Tab này đọc ĐÚNG dữ liệu ca ưu tiên của chính nhân viên đó.
 *
 * Không lọc theo chi nhánh đang chọn: nhân viên có thể có ca ưu tiên gắn chi nhánh khác, lọc theo
 * chi nhánh sẽ làm mất đúng dữ liệu cần xem.
 */

const weekdayLabels = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const weekdayNames = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

const pad2 = (n) => String(n ?? 0).padStart(2, '0');

/** Giờ bắt đầu "HH:MM". */
const startTimeText = (tpl) => (tpl ? `${pad2(tpl.start_hour)}:${pad2(tpl.start_minute)}` : '-');

/** Giờ kết thúc = giờ bắt đầu + thời lượng (phút), quấn vòng qua nửa đêm. */
const endTimeText = (tpl) => {
  if (!tpl) return '-';
  const total = (tpl.start_hour || 0) * 60 + (tpl.start_minute || 0) + (tpl.duration_minutes || 0);
  const minutes = ((total % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
};

const daysBadges = (days) => {
  const list = Array.isArray(days) ? [...days].sort() : [];
  if (!list.length) return <span className="text-muted small">Chưa chọn ngày nào</span>;
  return list.map((d) => (
    <Badge key={d} bg="primary" className="me-1" title={weekdayNames[d] || `Ngày ${d}`}>
      {weekdayLabels[d] || d}
    </Badge>
  ));
};

/** Trạng thái hiển thị: ưu tiên cờ tắt trước, rồi tới trạng thái duyệt (dữ liệu cũ #190405). */
const statusOf = (p) => {
  if (p.is_active === false) return { text: 'Tạm tắt', bg: 'secondary' };
  if (p.status === 'APPROVED') return { text: 'Đã duyệt', bg: 'success' };
  if (p.status === 'REJECTED') return { text: 'Đã từ chối', bg: 'danger' };
  if (p.status === 'PENDING') return { text: 'Chờ duyệt', bg: 'warning' };
  return { text: 'Đang áp dụng', bg: 'success' };
};

export default function EmployeeShiftPreferenceTab({ employeeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchPreferences = useCallback(async () => {
    if (!employeeId) {
      setRows([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const res = await shiftPreferenceAPI.getShiftPreferences({
        employee_id: employeeId,
        page: 1,
        limit: 1000,
      });
      if (requestIdRef.current !== requestId) return;
      const data = res?.data || res || [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setError(err?.message || 'Không thể tải ca ưu tiên của nhân viên');
      setRows([]);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const renderPreference = (p, idx) => {
    const tpl = p.shift_template;
    const status = statusOf(p);
    return { key: p.id || idx, tpl, status, p };
  };

  return (
    <div>
      {loading && (
        <div className="text-center py-4" role="status" aria-label="Đang tải">
          <Spinner size="sm" />
        </div>
      )}

      {!loading && error && (
        <Alert variant="danger" className="py-2 mb-0">
          {error}
        </Alert>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="text-center py-4">
          <p className="text-muted mb-0">
            Nhân viên này chưa đăng ký ca ưu tiên nào.
          </p>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          {/* Màn rộng: bảng đủ cột */}
          <div className="d-none d-md-block">
            <Table bordered size="sm" responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Ca ưu tiên</th>
                  <th>Khung giờ</th>
                  <th>Các ngày trong tuần</th>
                  <th>Chi nhánh</th>
                  <th>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, idx) => {
                  const { key, tpl, status } = renderPreference(p, idx);
                  return (
                    <tr key={key}>
                      <td className="fw-medium">{tpl?.name || 'Ca ưu tiên'}</td>
                      <td className="small text-nowrap">
                        {startTimeText(tpl)}–{endTimeText(tpl)}
                        <div className="text-muted">{tpl?.duration_minutes || 0} phút</div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">{daysBadges(p.days_of_week)}</div>
                      </td>
                      <td className="small">{p.branch?.name || '-'}</td>
                      <td>
                        <Badge bg={status.bg}>{status.text}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Khổ điện thoại: mỗi ca là 1 thẻ xếp dọc, không cuộn ngang */}
          <div className="d-md-none d-flex flex-column gap-2">
            {rows.map((p, idx) => {
              const { key, tpl, status } = renderPreference(p, idx);
              return (
                <div key={key} className="border rounded p-2">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <span className="fw-bold">{tpl?.name || 'Ca ưu tiên'}</span>
                    <Badge bg={status.bg}>{status.text}</Badge>
                  </div>
                  <div className="small mt-1">
                    <span className="text-muted">Khung giờ: </span>
                    {startTimeText(tpl)}–{endTimeText(tpl)}
                    <span className="text-muted"> ({tpl?.duration_minutes || 0} phút)</span>
                  </div>
                  <div className="small mt-1">
                    <div className="text-muted mb-1">Các ngày trong tuần:</div>
                    <div className="d-flex flex-wrap gap-1">{daysBadges(p.days_of_week)}</div>
                  </div>
                  <div className="small mt-1">
                    <span className="text-muted">Chi nhánh: </span>
                    {p.branch?.name || '-'}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
